import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import {
  FeatureAppDefinition,
  FeatureServices,
  Logger,
} from '@feature-hub/core';
import { HistoryServiceV1 } from '@feature-hub/history-service';
import { ReactFeatureApp } from '@feature-hub/react';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ServerRequestV1 } from '@feature-hub/server-request';
import { FaContentServiceV1 } from '@volkswagen-onehub/fa-content-service';
import { FeatureappServiceV1 } from '@volkswagen-onehub/featureapp-service';
import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';
import { LoaderSpinner, ThemeProvider } from '@vw-marketing/us-components';

import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';

import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';

import React, { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { FeatureAppEnvironmentContext } from './hooks/use-feature-app-environment';
import { HistoryContext } from './hooks/use-history';

import {
  FeatureAppMemoryStoreV1,
  featureAppMemoryStoreDefinition,
} from './hooks/feature-app-memory-store';

import { StoreModel } from './hooks-store/typings/store';

import { styled } from '@vw-marketing/us-components';
import { App } from './app';
import configureStore from './hooks-store/incentive-store';
import { cleanStore, seedDeeplinkState } from './hooks-store/store';
import { FeatureServicesContext } from './hooks/use-feature-services';
import LaunchDarklyProvider from './components/LaunchDarklyProvider';
import { fetchLaunchDarklyClientConfig } from './fetch-data/fetch-launch-darkly-config';
import {
  getDeeplinkParamsFromSearch,
  getSearchStringFromServerRequestUrl,
} from './utils/deeplink-params';

const StyledVersionContainer: any = styled.div`
  display: none;
`;

declare var __VERSION__: String;

export interface FeatureAppDependencies extends FeatureServices {
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:history': HistoryServiceV1;
  readonly 's2:logger'?: Logger;
  readonly 's2:serialized-state-manager': SerializedStateManagerV1;
  readonly 's2:server-request'?: ServerRequestV1;
  readonly 'fa-content-service'?: FaContentServiceV1;
  readonly 'featureapp-service': FeatureappServiceV1;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'service-config-provider': ServiceConfigProviderV1;
  readonly 'feature-app-memory-store': FeatureAppMemoryStoreV1<StoreModel>;
  readonly 'tracking'?: TrackingManagerV1;
}

export type FeatureAppMode = 'maintenance' | 'literature';

export interface FeatureAppConfig {
  readonly faHostServerUrl?: string;
  readonly faServicesUrl: string;
  readonly mockIds?: string;
  readonly mocksBaseUrl?: string;
  readonly mode: FeatureAppMode;
}

const ThemedApp: React.FC<{ baseUrl?: string }> = ({ baseUrl }) => {
  const { ciGuidelinesRollout } = useFlags();

  return (
    <ThemeProvider
      theme="main"
      variant={ciGuidelinesRollout ? 'v26' : undefined}
    >
      <StyledVersionContainer>
        Feature App Version :: Maintenance :: {__VERSION__}
      </StyledVersionContainer>
      <App baseUrl={baseUrl} />
    </ThemeProvider>
  );
};

const AppWithLaunchDarkly: React.FC<{ baseUrl?: string }> = ({ baseUrl }) => {
  const [clientId, setClientId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initLaunchDarkly = async () => {
      try {
        const ldKey = await fetchLaunchDarklyClientConfig(baseUrl);
        setClientId(ldKey);
      } catch (error) {
        console.error('Failed to fetch LaunchDarkly config:', error);
      } finally {
        setLoading(false);
      }
    };

    initLaunchDarkly();
  }, [baseUrl]);

  if (loading) {
    return (
      <ThemeProvider theme="main">
        <LoaderSpinner />
      </ThemeProvider>
    );
  }

  return (
    <LaunchDarklyProvider clientId={clientId}>
      <ThemedApp baseUrl={baseUrl} />
    </LaunchDarklyProvider>
  );
};

export const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  FeatureAppDependencies,
  FeatureAppConfig
> = {
  dependencies: {
    featureServices: {
      's2:history': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'feature-app-memory-store': '^1.0.0',
      'featureapp-service': '^1.3.0',
      'locale-service': '^1.0.0',
      'service-config-provider': '^1.0.0',
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:async-ssr-manager': '^1.0.0',
      's2:logger': '^1.0.0',
      's2:server-request': '^1.0.0',
      'tracking': '^1.0.0',
      'fa-content-service': '^1.0.0',
    },
  },
  ownFeatureServiceDefinitions: [featureAppMemoryStoreDefinition],

  create: (env) => {
    const { featureServices, baseUrl } = env;

    const {
      's2:async-ssr-manager': asyncSsrManager,
      's2:history': historyService,
      's2:serialized-state-manager': serializedStateManager,
      's2:server-request': serverRequest,
      'feature-app-memory-store': memoryStore,
    } = featureServices;

    const history = asyncSsrManager
      ? historyService.createStaticHistory()
      : historyService.createBrowserHistory();

    const serializedState = serializedStateManager.getSerializedState();

    if (serializedState && !memoryStore.getState()) {
      memoryStore.setState(JSON.parse(serializedState));
    }

    serializedStateManager.register(() =>
      JSON.stringify(memoryStore.getState()),
    );

    // Not read from `history.location.search`: the integrator's history
    // service isolates non-primary consumers' locations (this feature app
    // isn't the primary consumer), so raw `utm_*` query params never reach
    // it there. Read the real request URL directly instead.
    const search =
      typeof window !== 'undefined'
        ? window.location.search
        : getSearchStringFromServerRequestUrl(serverRequest?.url ?? '');
    const deeplinkParams = getDeeplinkParamsFromSearch(search);

    cleanStore(memoryStore);
    seedDeeplinkState(memoryStore, deeplinkParams);
    configureStore();

    return {
      render: () => (
        <HistoryContext.Provider value={history}>
          <FeatureAppEnvironmentContext.Provider value={env}>
            <FeatureServicesContext.Provider value={featureServices}>
              <AppWithLaunchDarkly baseUrl={baseUrl} />
            </FeatureServicesContext.Provider>
          </FeatureAppEnvironmentContext.Provider>
        </HistoryContext.Provider>
      ),
    };
  },
};

export default featureAppDefinition;
